import { useLazyQuery } from "@apollo/client";
import { Button, Paper, Stack, Text, Title } from "@mantine/core";
import { useState } from "react";
import { Product, ProductInput } from "../../../gql/__generated__/graphql";
import { GET_SUGGESTED_BUDGET } from "../../apis/lambda/GraphQL/Collab/queries";

// Define the different calculation modes
export type BudgetCalculationMode = "brand" | "campaign" | "custom";

// Base props for all calculation modes
interface BaseBudgetCalculatorProps {
  isValid: boolean;
  onBudgetCalculated: (suggestedBudget: number) => void;
}

// Creator mode specific props
interface BrandModeProps {
  mode: "brand";
  email: string;
  videosDesired: number;
  products: Product[];
}

// Campaign mode specific props
interface CampaignModeProps {
  mode: "campaign";
  videosDesired: number;
  affiliateGMVMinimum: boolean;
  productsCount: number;
}

// Custom mode for future expansion
interface CustomModeProps {
  mode: "custom";
  // Add custom fields as needed
}

// Union type for all possible props
type BudgetCalculatorProps = BaseBudgetCalculatorProps &
  (BrandModeProps | CampaignModeProps | CustomModeProps);

function BudgetCalculator(props: BudgetCalculatorProps) {
  // React state
  const [suggestedBudget, setSuggestedBudget] = useState<number | null>(null);
  const [isCalculatingBudget, setIsCalculatingBudget] = useState(false);

  // GraphQL
  const [getSuggestedBudget] = useLazyQuery(GET_SUGGESTED_BUDGET, {
    onError: (error) => {
      console.error("Error fetching suggested budget:", error);
    },
  });

  // Calculate budget based on creator parameters
  const calculateCreatorBudget = async (
    creatorEmail: string,
    videosDesired: number
  ) => {
    if (props.mode !== "brand") return;
    setIsCalculatingBudget(true);

    try {
      const response = await getSuggestedBudget({
        variables: {
          input: {
            email: creatorEmail,
            numVideos: videosDesired,
            products: props.products.map(
              // @ts-ignore
              ({ __typename, createdAt, updatedAt, ...rest }) => rest
            ) as ProductInput[],
          },
        },
      });

      const data = response.data?.getSuggestedBudget;
      if (data) {
        setSuggestedBudget(data);
        props.onBudgetCalculated(data);
      }
    } catch (error) {
      console.error("Error in GraphQL query:", error);
    }

    setIsCalculatingBudget(false);
  };

  // Calculate budget based on campaign parameters
  const calculateCampaignBudget = async (
    videosDesired: number,
    affiliateGMVMinimum: boolean,
    productsCount: number
  ) => {
    setIsCalculatingBudget(true);

    // try {
    //   const response = await getSuggestedBudget({
    //     variables: {
    //       videosDesired,
    //       productsCount,
    //       affiliateGMVMinimum,
    //     },
    //   });

    //   const data = response.data?.getSuggestedBudget;
    //   if (data) {
    //     setSuggestedBudget(data);
    //     props.onBudgetCalculated(data);
    //     setIsCalculatingBudget(false);
    //     return;
    //   }
    // } catch (error) {
    //   console.error("Error in GraphQL query:", error);
    // }

    // Fallback: Simple budget calculation logic if GraphQL fails
    // Base: $20 per video, $30 per stream
    // Add 20% if GMV minimum is set
    // Add $10 per product beyond the first one
    setTimeout(() => {
      const videoCost = videosDesired * 20;
      // const streamCost = streamsDesired * 30;
      const streamCost = 0;
      const gmvFactor = affiliateGMVMinimum ? 1.2 : 1;
      const productFactor = (productsCount - 1) * 10;

      const calculatedBudget = Math.round(
        (videoCost + streamCost) * gmvFactor + productFactor
      );

      setSuggestedBudget(calculatedBudget);
      props.onBudgetCalculated(calculatedBudget);

      setIsCalculatingBudget(false);
    }, 1000);
  };

  // Handle budget calculation based on the current mode
  const handleCalculateBudget = async () => {
    if (props.mode === "brand") {
      await calculateCreatorBudget(props.email, props.videosDesired);
    } else if (props.mode === "campaign") {
      await calculateCampaignBudget(
        props.videosDesired,
        props.affiliateGMVMinimum,
        props.productsCount
      );
    }
    // Add other modes as needed
  };

  return (
    <Stack gap="md">
      {/* Mode selector - only show if needed for UI switching */}
      {/* 
      <Select
        label="Calculation Mode"
        value={calculationMode}
        onChange={(value) => setCalculationMode(value as BudgetCalculationMode)}
        data={[
          { value: 'creator', label: 'Creator Budget' },
          { value: 'campaign', label: 'Campaign Budget' },
        ]}
      />
      */}

      <Button
        onClick={handleCalculateBudget}
        disabled={!props.isValid}
        loading={isCalculatingBudget}
        color="blue"
        variant="outline"
        fullWidth
      >
        Determine Suggested Budget
      </Button>

      {suggestedBudget !== null && (
        <Paper p="md" withBorder bg="blue.0">
          <Stack gap="xs">
            <Title order={4}>Suggested Budget</Title>
            <Text size="xl" fw={700} c="blue">
              ${suggestedBudget.toLocaleString()}
            </Text>
            <Text size="sm">
              This budget is calculated based on your requirements and will be
              used to compensate the{" "}
              {props.mode === "brand" ? "creator" : "campaign"}.
            </Text>
          </Stack>
        </Paper>
      )}
    </Stack>
  );
}

export default BudgetCalculator;
